import { useState, useEffect } from "react";
import FleetWebApi from "../Api/FleetWebApi";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import { TableContainer, TableHead } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Moment from "react-moment";
// import { withStyles } from "@mui/styles";
import Proxies from "./Proxies";
import HarnessId from "./HarnessId";
import CircularProgress from "@mui/material/CircularProgress";
import RmaDeviceSummary from "./RmaDeviceSummary";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ParticleApi from "../Api/ParticleApi";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import MfgWebNotes from "./MfgWebNotes";
import badShutties from "../Configuration/BadShutties";
import getDisplayDeviceType from "../Configuration/DisplayDeviceType";
import alertLabsApi from "../Api/AlertLabsApi";
import { useAuthDataContext } from "../AuthDataProvider";

// const styles = (theme) => ({
//   root: {
//     width: "100%",
//     marginTop: theme.spacing(3),
//     overflowX: "auto",
//   },
// });

function DeviceAlertLabsDetails(props) {
  const [isFwLoaded, setIsFwLoaded] = useState(false);
  const [FwDetails, setFwDetails] = useState(null);
  const [FwError, setFwError] = useState(null);
  const [particle, setParticle] = useState(null);
  const [importantNotes, setImportantNotes] = useState(null);
  const [importantNotesError, setImportantNotesError] = useState(null);
  const [activeConfigs, setActiveConfigs] = useState(null);
  const [activeConfigsError, setActiveConfigsError] = useState(null);
  const [lastHeard, setLastHeard] = useState(null);
  const [sensorFirstSeen, setSensorFirstSeen] = useState(null);
  const [virtualSensorID, setVirtualSensorID] = useState(null);
  const [isVirtualIdLoaded, setIsVirtualIdLoaded] = useState(false);
  const { user } = useAuthDataContext();

  let affectedFw = props.affectedFw;
  let affectedRev = props.affectedRev;
  let deviceDetails = props.deviceDetails;
  let productId = props.productId;
  let deviceDetailsContainer;
  let deviceRmaContainer;
  let mfgWebNotesContainer;
  let importantNotesContainer;
  let activeConfigContainer;
  
  // Fetch virtual sensor ID once at the beginning
  useEffect(() => {
    let isMounted = true;
    const coreId = deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id;
    
    const fetchVirtualSensorID = async () => {
      if (user && user.token) {
        try {
          const virtualIDs = await alertLabsApi.getVirtualSensorID(coreId, user.token);
          if (isMounted && virtualIDs?.[0] && virtualIDs[0] !== coreId) {
            setVirtualSensorID(virtualIDs[0]);
          }
        } catch (error) {
          console.log("No virtual ID found for device");
        }
      }
      if (isMounted) {
        setIsVirtualIdLoaded(true);
      }
    };
    
    fetchVirtualSensorID();
    
    return () => {
      isMounted = false;
      setVirtualSensorID(null);
      setIsVirtualIdLoaded(false);
    };
  }, [deviceDetails.coreID, deviceDetails._id, user]);
  
  useEffect(() => {
    if (!isVirtualIdLoaded) return; // Wait for virtual ID to be fetched
    
    let isMounted = true;
    const coreId = deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id;
    
    // Call API with both core ID and virtual ID 
    FleetWebApi.getDeviceFirmwareDetails(coreId, virtualSensorID).then(
      (fw_result) => {
        if (isMounted) {
          setIsFwLoaded(true);
          setFwDetails(fw_result);
        }
      },
      (fw_error) => {
        if (isMounted) {
          setIsFwLoaded(true);
          setFwError("Error Loading Firmware from Db");
        }
      }
    );
    
    return () => {
      isMounted = false;
      setIsFwLoaded(false);
      setFwError(null);
      setFwDetails(null);
    };
  }, [deviceDetails.coreID, deviceDetails._id, virtualSensorID, isVirtualIdLoaded]);

  useEffect(() => {
    let isMounted = true;
    const coreId = deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id;
    FleetWebApi.getDeviceLastHeard(coreId)
      .then((result) => {
        if (isMounted) setLastHeard(result);
      })
      .catch(() => {
        if (isMounted) setLastHeard(null);
      });
    return () => {
      isMounted = false;
      setLastHeard(null);
    };
  }, [deviceDetails.coreID, deviceDetails._id]);

  useEffect(() => {
    if (!isVirtualIdLoaded) return; // Wait for virtual ID to be fetched
    
    let isMounted = true;
    const coreId = deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id;
    const sensorId = virtualSensorID || coreId;
    
    if (user && user.token) {
      alertLabsApi.getSensorDetails(sensorId, user.token)
        .then((result) => {
          if (isMounted) setSensorFirstSeen(result?.firstSeen ?? null);
        })
        .catch(() => {
          if (isMounted) setSensorFirstSeen(null);
        });
    } else {
      if (isMounted) setSensorFirstSeen(null);
    }
    
    return () => {
      isMounted = false;
      setSensorFirstSeen(null);
    };
  }, [deviceDetails.coreID, deviceDetails._id, virtualSensorID, isVirtualIdLoaded, user]);

  useEffect(() => {
    let isMounted = true;
    if (productId) {
      ParticleApi.getDeviceDetails(
        productId,
        deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id
      ).then(
        (particle_result) => {
          if (isMounted) {
            setParticle(particle_result.response);
          }
        },
        (particle_error) => {
          console.log(particle_error);
        }
      );
    }
    return () => {
      // clean the state
      isMounted = false;
      setParticle(null);
    };
  }, [productId, deviceDetails.coreID, deviceDetails._id]);
  useEffect(() => {
    let isMounted = true;
    FleetWebApi.getActiveConfigurations(
      deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id
    ).then(
      (cfg_result) => {
        if (isMounted) {
          setActiveConfigs(cfg_result);
        }
      },
      (cfg_error) => {
        if (isMounted) {
          setActiveConfigsError(cfg_error);
        }
      }
    );
    return () => {
      // clean the state
      isMounted = false;
      setActiveConfigs(null);
      setActiveConfigsError(null);
    };
  }, [deviceDetails.coreID, deviceDetails._id]);
  useEffect(() => {
    let isMounted = true;
    FleetWebApi.getAllNotes(
      deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id,
      "Device",
      0,
      50,
      true
    )
      .then()
      .then(
        (notes_result) => {
          if (isMounted) {
            setImportantNotes(notes_result);
          }
        },
        (notes_error) => {
          if (isMounted) {
            setImportantNotesError(notes_error);
          }
        }
      );
    return () => {
      // clean the state
      isMounted = false;
      setImportantNotes(null);
      setImportantNotesError(null);
    };
  }, [deviceDetails.coreID, deviceDetails._id]);
  deviceDetailsContainer = (
    <TableBody>
      <TableRow>
        <TableCell component="th" scope="row">
          Serial
        </TableCell>
        <TableCell data-th="Serial">
          <span style={{ marginRight: 4 }}>
            {(deviceDetails.serial ? deviceDetails.serial : deviceDetails._id)
              .match(/.{3}/g)
              .join(".")}
          </span>
          {deviceDetails.serial ? (
            <Tooltip title="MfgWeb" arrow placement="top">
              <IconButton size="small" style={{ marginLeft: 5, padding: 1 }}>
                <a
                  href={
                    "https://mfgweb.alertlabs.com/device/" + deviceDetails.serial
                  }
                  rel="noopener noreferrer"
                  target="_blank"
                  className="mfgweb"
                >
                  <img src="/mfg.png" alt="MfgWeb" style={{ width: 16, height: 16, display: "block" }} />
                </a>
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="MfgWeb" arrow placement="top">
              <IconButton size="small" style={{ marginLeft: 5, padding: 1 }}>
                <a
                  href={
                    "https://mfgweb.alertlabs.com/device/" + deviceDetails._id
                  }
                  rel="noopener noreferrer"
                  target="_blank"
                  className="mfgweb"
                >
                  <img src="/mfg.png" alt="MfgWeb" style={{ width: 16, height: 16, display: "block" }} />
                </a>
              </IconButton>
            </Tooltip>
          )}
          <CoreParticleConsoleLink coreID={deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id} />
          <AlertAQLinkButton serial={deviceDetails.serial || deviceDetails._id} />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell component="th" scope="row">
          Core Id
        </TableCell>
        <TableCell data-th="Core ID">
          {deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell component="th" scope="row">
          Manufacturing Date
        </TableCell>
        <TableCell data-th="Manufacturing Date">
          {deviceDetails.manufacturedOn ? (
            <Moment format="YYYY-MM-DD HH:mm:ss Z">
              {deviceDetails.manufacturedOn}
            </Moment>
          ) : (
            <div>-</div>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell component="th" scope="row">
          Type
        </TableCell>
        <TableCell data-th="Type">{getDisplayDeviceType(deviceDetails)}</TableCell>
      </TableRow>
      <Proxies
        coreId={
          deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id
        }
        type={deviceDetails.type}
      />
      <TableRow>
        <TableCell component="th" scope="row">
          First Seen
        </TableCell>
        <TableCell data-th="First Seen">
          {sensorFirstSeen ? (
            <Moment format="YYYY-MM-DD HH:mm:ss Z">
              {sensorFirstSeen}
            </Moment>
          ) : (
            <div>-</div>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell component="th" scope="row">
          Last Seen
        </TableCell>
        <TableCell data-th="Last Seen">
          {lastHeard ? (
            <Moment format="YYYY-MM-DD HH:mm:ss Z">{lastHeard}</Moment>
          ) : (
            <div>-</div>
          )}
        </TableCell>
      </TableRow>
      <HarnessId
        coreId={deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id}
        type={deviceDetails.type}
      />
    </TableBody>
  );

  let deviceFirmwareContainer;
  if (!isFwLoaded) {
    <div>
      <CircularProgress color="secondary" />
    </div>;
  } else if (FwError && !particle) {
    deviceFirmwareContainer = (
      <TableBody>
        <TableRow>
          <TableCell>{FwError}</TableCell>
        </TableRow>
      </TableBody>
    );
  } else {
    var AlertLabsFirmware, pendingFirmware;
    if (
      FwDetails &&
      FwDetails.alert_labs_software_version &&
      particle &&
      particle.firmware_version
    ) {
      AlertLabsFirmware =
        FwDetails.alert_labs_software_version +
        " / V" +
        particle.firmware_version;
    } else if (FwDetails && FwDetails.alert_labs_software_version) {
      AlertLabsFirmware = FwDetails.alert_labs_software_version;
    } else {
      AlertLabsFirmware = "error";
    }
    if (particle && particle.targeted_firmware_release_version) {
      pendingFirmware = "V" + particle.targeted_firmware_release_version;
    } else {
      pendingFirmware = null;
    }
    deviceFirmwareContainer = (
      <TableBody>
        {(deviceDetails.type === "WaterMeter" ||
          deviceDetails.type === "Hubbie" ||
          deviceDetails.type === "SumpPit" ||
          deviceDetails.type === "Sentree") &&
          FwDetails &&
          FwDetails.particle_software_version ? (
          <TableRow>
            <TableCell component="th" scope="row">
              Particle OS
            </TableCell>
            <TableCell component="th" scope="row">
              {FwDetails.particle_software_version}
            </TableCell>
          </TableRow>
        ) : null}
        {FwDetails && FwDetails.alert_labs_software_version ? (
          <TableRow>
            <TableCell component="th" scope="row">
              FW Version
            </TableCell>
            <TableCell component="th" scope="row">
              {AlertLabsFirmware}
            </TableCell>
          </TableRow>
        ) : null}
        {pendingFirmware ? (
          <TableRow>
            <TableCell component="th" scope="row">
              Desired Firmware Version
            </TableCell>
            <TableCell component="th" scope="row">
              {pendingFirmware}
            </TableCell>
          </TableRow>
        ) : null}

        {FwDetails && FwDetails.timestamp ? (
          <TableRow>
            <TableCell component="th" scope="row">
              Last FW Change
            </TableCell>
            <TableCell component="th" scope="row">
              <Moment format="YYYY-MM-DD HH:mm:ss Z">
                {FwDetails.timestamp}
              </Moment>
            </TableCell>
          </TableRow>
        ) : null}
        {FwDetails &&
        (FwDetails.ext_cable === 1 ||
          FwDetails.ts === 4 ||
          FwDetails.ws === 1 ||
          FwDetails.ws === 2 ||
          FwDetails.ws === 3) ? (
          <TableRow>
            <TableCell component="th" scope="row">
              Accessory
            </TableCell>
            <TableCell>
              {FwDetails.ext_cable === 1
                ? "Flood Cable"
                : FwDetails.ts === 4
                  ? "Temp Cable"
                  : FwDetails.ws === 1
                    ? "Onboard Mag"
                    : FwDetails.ws === 2
                      ? "External Mag"
                      : FwDetails.ws === 3
                        ? "Pulse Cable"
                        : null}
            </TableCell>
          </TableRow>
        ) : null}
      </TableBody>
    );
    deviceRmaContainer = (
      <RmaDeviceSummary
        serial={(deviceDetails.serial
          ? deviceDetails.serial
          : deviceDetails._id
        )
          .match(/.{3}/g)
          .join("")}
      />
    );
    mfgWebNotesContainer = (
      <MfgWebNotes
        serial={(deviceDetails.serial
          ? deviceDetails.serial
          : deviceDetails._id
        )
          .match(/.{3}/g)
          .join("")}
      />
    );
    importantNotesContainer = (
      <Paper>
        {importantNotes && importantNotes.length !== 0 ? (
          <TableContainer sx={{ maxHeight: "50vh" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Note</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {importantNotes.map((note, i) => {
                  return (
                    <TableRow key={i + "tr"}>
                      <TableCell key={i + "tc1"}>
                        <Moment
                          key={i + "tc1moment"}
                          format="YYYY-MM-DD HH:mm:ss Z"
                        >
                          {note.timestamp}
                        </Moment>
                      </TableCell>
                      <TableCell key={i + "tc2"}>{note.user_name}</TableCell>
                      <TableCell key={i + "tc3"}>{note.note}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : importantNotesError ? (
          importantNotesError.message
        ) : (
          "No Important Notes found. Check Notes tab for all available notes."
        )}
      </Paper>
    );
    activeConfigContainer = (
      <Paper>
        {activeConfigs && Object.keys(activeConfigs).length !== 0 ? (
          <TableContainer sx={{ maxHeight: "25vh" }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Key</TableCell>
                  <TableCell>Value</TableCell>
                  <TableCell>Value type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(activeConfigs).map((key) => {
                  return (
                    <TableRow key={key + "tr"}>
                      <TableCell key={key + "tc1"}>{key}</TableCell>
                      <TableCell key={key + "tc2"}>
                        {activeConfigs[key]}
                      </TableCell>
                      <TableCell key={key + "tc3"}>
                        {typeof activeConfigs[key]}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : activeConfigsError ? (
          activeConfigsError.message
        ) : (
          "No Active Overrides"
        )}
      </Paper>
    );
  }
  return (
    <Container maxWidth="xl">
      <Grid container spacing={3}>
        {deviceDetails ? (
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <h3>Device Details</h3>
            {deviceDetails.doNotRegister ? (
              <Alert severity="warning">
                <AlertTitle>Do Not Register This Device!</AlertTitle>
                This device is flagged in the database and should not be
                registered
              </Alert>
            ) : null}
            {deviceDetails.doNotIngest ? (
              <Alert severity="warning">
                <AlertTitle>Device Paused - Data is not currently being ingested</AlertTitle>
              </Alert>
            ) : null}
            {affectedRev && (deviceDetails.serial || deviceDetails._id)
              ? affectedRev.map((rev, idx) => {
                return rev.key === (deviceDetails.serial ? deviceDetails.serial[1] : deviceDetails._id[1]) ? (
                  <Alert severity="warning" key={idx + "alert"}>
                    <AlertTitle key={idx + "title"}>
                      Known issue with this revision!
                    </AlertTitle>
                    {rev.badAffect}
                  </Alert>
                ) : null;
              })
              : null}
            {/* SPECIAL BANNER FOR SHUTTIE RADIO PROBLEM --- START*/}
            {deviceDetails && deviceDetails.type && deviceDetails.type == "Shuttie" && badShutties.includes(deviceDetails.coreID ? deviceDetails.coreID : deviceDetails._id)
              ? <Alert severity="warning">
                <AlertTitle>
                  CoreID flagged for Shuttie 2024-07 Communication Issue!
                </AlertTitle>
              </Alert>
              : null}
            {/* END */}
            <Paper>
              <TableContainer sx={{ maxHeight: "50vh" }}>
                <Table classes={{ root: "reactivetable" }}>
                  {deviceDetails ? deviceDetailsContainer : null}
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        ) : null}
        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
          <h3>Notes Highlights</h3>
          {importantNotesContainer}
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
          <h3>Extras</h3>
          {affectedFw && FwDetails && FwDetails.alert_labs_software_version
            ? affectedFw.map((fw, idx) => {
              return fw.key === FwDetails.alert_labs_software_version ? (
                <Alert severity="warning" key={idx + "alert"}>
                  <AlertTitle key={idx + "title"}>
                    Known issue with this firmware!
                  </AlertTitle>
                  {fw.badAffect}
                </Alert>
              ) : null;
            })
            : null}
          <Paper>
            <Table classes={{ root: "reactivetable" }}>
              {deviceDetails ? deviceFirmwareContainer : null}
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
          <h3>Active Overrides</h3>
          {activeConfigContainer}
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
          {deviceRmaContainer}
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
          {mfgWebNotesContainer}
        </Grid>
      </Grid>
    </Container>
  );
}

function AlertAQLinkButton({ serial }) {
  if (!serial) return null;
  const href = `https://alertaq.com/quickportal?tab=Sensors&qSerialNumber=${serial}`;
  return (
    <Tooltip title="AlertAQ" arrow placement="top">
      <IconButton
        size="small"
        style={{ marginLeft: 5, padding: 1 }}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open in AlertAQ"
      >
        <img src="/aq_logo.png" alt="AlertAQ" style={{ width: 16, height: 16, display: "block" }} />
      </IconButton>
    </Tooltip>
  );
}

function CoreParticleConsoleLink({ coreID }) {
  const [slug, setSlug] = useState(null);
  useEffect(() => {
    let mounted = true;
    FleetWebApi.getProductSlug(coreID)
      .then((response) => {
        if (mounted) setSlug(response);
      })
      .catch(() => {
        if (mounted) setSlug(null);
      });
    return () => {
      mounted = false;
    };
  }, [coreID]);
  if (!slug) return null; // only particle devices
  const href = `https://console.particle.io/${slug}/devices/${coreID}`;
  return (
    <Tooltip title="Particle" arrow placement="top">
      <IconButton
        size="small"
        style={{ marginLeft: 5, padding: 1 }}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open in Particle Console"
      >
        <img src="/particle_logo.png" alt="Particle" style={{ width: 16, height: 16, display: "block" }} />
      </IconButton>
    </Tooltip>
  );
}

// export default withStyles(styles, { withTheme: true })(DeviceAlertLabsDetails);
export default DeviceAlertLabsDetails;
